/**
 * `node:http` — `createServer` hands its request handler to the demo instead of a socket.
 *
 * ⭐ This is what lets the demo run src/web/server.ts unmodified: the page's fetch('/api/…') is
 * routed into the same handler `sibyl web` serves, through a request/response pair built here.
 */
type Handler = (req: FakeReq, res: FakeRes) => void
export let lastHandler: Handler | undefined

export interface FakeReq {
  url: string
  method: string
  headers: Record<string, string>
  on(ev: string, fn: (arg?: unknown) => void): FakeReq
  destroy(): void
}
export interface FakeRes {
  headersSent: boolean
  writeHead(code: number, headers?: Record<string, string>): void
  end(body?: string | Uint8Array): void
}

export function createServer(handler: Handler) {
  lastHandler = handler
  const closers: (() => void)[] = []
  return {
    listen(): void {},
    on(ev: string, fn: () => void) {
      if (ev === "close") closers.push(fn)
    },
    close(): void {
      for (const c of closers) c()
    },
  }
}

export function dispatch(handler: Handler, url: string, method: string, body: string): Promise<{ status: number; headers: Record<string, string>; body: string }> {
  return new Promise((resolve) => {
    const listeners: Record<string, ((arg?: unknown) => void)[]> = {}
    const req: FakeReq = {
      url,
      method,
      headers: { "content-type": "application/json" },
      on(ev, fn) {
        ;(listeners[ev] ??= []).push(fn)
        return req
      },
      destroy() {},
    }
    let status = 200
    let headers: Record<string, string> = {}
    const res: FakeRes = {
      headersSent: false,
      writeHead(code, h) {
        status = code
        headers = h ?? {}
        res.headersSent = true
      },
      end(b) {
        resolve({ status, headers, body: typeof b === "string" ? b : b === undefined ? "" : new TextDecoder().decode(b) })
      },
    }
    handler(req, res)
    queueMicrotask(() => {
      if (body !== "") for (const fn of listeners["data"] ?? []) fn(new TextEncoder().encode(body))
      for (const fn of listeners["end"] ?? []) fn()
    })
  })
}
export const request = (): never => {
  throw new Error("node:http request is not available in the browser demo")
}
export default { createServer, request }
